import { required, useGetIdentity } from "ra-core";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { DateInput } from "@/components/admin/date-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Sale, SponsoringKind } from "../types";
import { getBoardStages } from "./stages";

const saleOptionText = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

// SelectInput cannot hold null itself: map the empty option to null
const nullableParse = (value: unknown) => (value === "" ? null : value);
const nullableFormat = (value: unknown) => value ?? "";

/**
 * Form fields shared by the create and edit dialogs: Bedrijf*, Contracttype,
 * Fase*, Einddatum, Behandelaar, Opmerkingen. A balsponsor has no contract
 * type, so that input is left out; its `kind` stays in the form values (it
 * comes from the record, or from the default values of the create dialog) and
 * is saved along with the rest.
 */
export const SponsoringInputs = () => {
  const config = useConfigurationContext();
  const { identity } = useGetIdentity();
  const kind = useWatch<{ kind?: SponsoringKind }>({ name: "kind" });
  const defaultStage = useMemo(
    () => getBoardStages(config)[0]?.value ?? config.sponsoringStages[0]?.value,
    [config],
  );

  return (
    <div className="flex flex-col gap-4">
      <ReferenceInput source="company_id" reference="companies">
        <AutocompleteCompanyInput
          label="resources.sponsorings.fields.company_id"
          validate={required()}
          modal
        />
      </ReferenceInput>
      {kind === "ball" ? null : (
        <ReferenceInput
          source="contract_type_id"
          reference="contract_types"
          filter={{ active: true }}
          sort={{ field: "sort_order", order: "ASC" }}
        >
          <SelectInput
            optionText="name"
            emptyText="resources.sponsorings.no_contract_type"
            parse={nullableParse}
            format={nullableFormat}
            helperText={false}
          />
        </ReferenceInput>
      )}
      <SelectInput
        source="stage"
        choices={config.sponsoringStages}
        optionText="label"
        optionValue="value"
        defaultValue={defaultStage}
        validate={required()}
        helperText={false}
      />
      <DateInput source="end_date" helperText={false} />
      <ReferenceInput
        source="sales_id"
        reference="sales"
        sort={{ field: "last_name", order: "ASC" }}
        filter={{ "disabled@neq": true }}
      >
        <SelectInput
          optionText={saleOptionText}
          emptyText="resources.sponsorings.no_owner"
          parse={nullableParse}
          format={nullableFormat}
          defaultValue={identity?.id}
          helperText={false}
        />
      </ReferenceInput>
      <TextInput source="description" multiline rows={4} helperText={false} />
    </div>
  );
};
